/* ============================================================
   ARCHIVE 135 — theme.js
   Minimal: mobile menu (focus trap), product gallery, qty, cart page.

   Every feature is a small init() that is safe to run again. The theme editor
   re-renders one section at a time and fires shopify:section:load; anything
   bound once at parse time is left listening to markup that no longer exists,
   which is how the gallery, steppers, video and cart triggers used to die in
   the editor. claim() is what keeps a second run from double-binding.
   ============================================================ */

(function () {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const FETCH_TIMEOUT = 10000;

  /* ---------- one-time binding bookkeeping ----------
     Keyed per feature because a single element can belong to two of them (the
     variant <select> is both a form control and a sticky-bar input source).
     A WeakMap rather than data-* attributes: nothing is stamped into markup the
     theme editor round-trips, and a node dropped by a re-render is collected. */
  const boundFeatures = new WeakMap();

  function claim(el, feature) {
    if (!el) return false;
    let features = boundFeatures.get(el);
    if (!features) {
      features = {};
      boundFeatures.set(el, features);
    }
    if (features[feature]) return false;
    features[feature] = true;
    return true;
  }

  // A hung request must not hang the UI forever. AbortSignal.timeout is missing
  // on older Safari, where undefined simply means "no timeout", as before.
  function timeoutSignal() {
    if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') return undefined;
    return AbortSignal.timeout(FETCH_TIMEOUT);
  }

  /* ---------- Overlay chrome shared by the menu and the cart drawer ----------
     Both overlays lock the page the same way, and assets/cart-drawer.js runs an
     identical pair of helpers. The state is DERIVED from the DOM rather than
     kept in a counter, so closing one overlay while the other is still open
     cannot unlock the page underneath it. */
  const OVERLAY_SELECTOR = '.cartd.is-open, .mobile-menu.is-open, .product-lightbox[open], .film-lightbox[open]';

  function syncOverlayChrome() {
    const overlays = Array.prototype.slice.call(document.querySelectorAll(OVERLAY_SELECTOR));
    // base.css sets html,body{overflow-x:clip}. Per CSS Overflow, body overflow
    // only propagates to the viewport while html's overflow is visible, so the
    // usual document.body.style.overflow='hidden' is a NO-OP on this theme.
    // Lock the root element instead (class defined in layout/theme.liquid).
    document.documentElement.classList.toggle('cartd-locked', overlays.length > 0);

    // Everything outside the open overlay is inert, so Tab and the screen
    // reader's virtual cursor cannot walk into the page behind it. Only body's
    // direct children are touched, and never the branch holding an overlay.
    Array.prototype.forEach.call(document.body.children, function (child) {
      const holdsOverlay = overlays.some(function (overlay) {
        return child.contains(overlay);
      });
      if (overlays.length > 0 && !holdsOverlay) {
        child.setAttribute('inert', '');
      } else {
        child.removeAttribute('inert');
      }
    });
  }

  /* ---------- Mobile menu ---------- */
  let lastFocusedBeforeMenu = null;

  function getMenu() {
    return document.querySelector('[data-mobile-menu]');
  }

  function getFocusable(root) {
    return Array.prototype.slice.call(root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  }

  function trapFocus(e) {
    const menu = getMenu();
    if (!menu || !menu.classList.contains('is-open')) return;
    if (e.key !== 'Tab') return;
    const focusable = getFocusable(menu);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function openMenu() {
    const menu = getMenu();
    if (!menu) return;
    lastFocusedBeforeMenu = document.activeElement;
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-menu-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });
    // Inerts the header, which holds the button that currently has focus, so
    // move focus into the menu straight afterwards.
    syncOverlayChrome();
    const first = getFocusable(menu)[0];
    if (first) first.focus();
    document.addEventListener('keydown', onMenuKeydown);
  }

  function closeMenu() {
    const menu = getMenu();
    if (!menu) return;
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-menu-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    document.removeEventListener('keydown', onMenuKeydown);
    // Must run BEFORE the focus restore: focus() is a no-op on an inert node.
    syncOverlayChrome();
    if (lastFocusedBeforeMenu && document.contains(lastFocusedBeforeMenu)) {
      lastFocusedBeforeMenu.focus();
    }
  }

  function onMenuKeydown(e) {
    if (e.key === 'Escape') closeMenu();
    trapFocus(e);
  }

  function initMobileMenu() {
    document.querySelectorAll('[data-menu-open]').forEach(function (btn) {
      if (claim(btn, 'menu')) btn.addEventListener('click', openMenu);
    });
    document.querySelectorAll('[data-menu-close]').forEach(function (btn) {
      if (claim(btn, 'menu')) btn.addEventListener('click', closeMenu);
    });
    document.querySelectorAll('[data-menu-link]').forEach(function (link) {
      if (claim(link, 'menu')) link.addEventListener('click', closeMenu);
    });
  }

  /* ---------- Small delegated actions ---------- */
  function initSmallActions() {
    document.querySelectorAll('[data-print-page]').forEach(function (button) {
      if (!claim(button, 'print')) return;
      button.addEventListener('click', function () { window.print(); });
    });

    document.querySelectorAll('[data-account-toggle]').forEach(function (trigger) {
      if (!claim(trigger, 'account')) return;
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        const showSelector = trigger.getAttribute('data-account-toggle');
        const hideSelector = trigger.getAttribute('data-account-hide');
        if (!showSelector || !hideSelector) return;
        const showTarget = document.querySelector(showSelector);
        const hideTarget = document.querySelector(hideSelector);
        if (!showTarget || !hideTarget) return;
        showTarget.hidden = false;
        hideTarget.hidden = true;
        const focusTarget = showTarget.querySelector('input, button, a[href]');
        if (focusTarget) focusTarget.focus();
      });
    });

    document.querySelectorAll('[data-delete-address]').forEach(function (button) {
      if (!claim(button, 'address')) return;
      button.addEventListener('click', function () {
        const url = button.getAttribute('data-delete-address');
        if (!url || !window.Shopify || typeof window.Shopify.postLink !== 'function') return;
        if (window.confirm('Delete this address?')) {
          window.Shopify.postLink(url, { parameters: { _method: 'delete' } });
        }
      });
    });
  }

  /* ---------- Homepage motion: scroll-driven frame sequence ----------
     The section is a tall runway with a sticky inner block. Scroll progress
     across the runway (0..1) selects a frame from an image sequence and draws
     it to a canvas, so the canister opens exactly as far as the page has been
     scrolled. Frames load coarse-to-fine (every 8th, then 4th, 2nd, all) so the
     first scrub is watchable before the whole sequence has arrived; a frame
     that has not loaded yet falls back to the nearest one that has. Without a
     canvas, or with prefers-reduced-motion, the section keeps its static
     layout and the ordinary autoplay video (initHomeVideo) takes over. */
  function initScrub() {
    document.querySelectorAll('[data-scrub]').forEach(function (section) {
      if (!claim(section, 'scrub')) return;
      const canvas = section.querySelector('[data-scrub-canvas]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!canvas || !canvas.getContext || reducedMotion.matches) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const count = Math.max(2, parseInt(section.getAttribute('data-scrub-frames'), 10) || 2);
      const template = section.getAttribute('data-scrub-frame-url') || '';
      if (template.indexOf('001') === -1) return;
      const frames = new Array(count);
      const beats = Array.prototype.map.call(section.querySelectorAll('[data-beat]'), function (el) {
        return { el: el, from: parseFloat(el.getAttribute('data-from')) || 0, to: parseFloat(el.getAttribute('data-to')) || 1 };
      });
      const timeLabel = section.querySelector('[data-scrub-time]');
      const bar = section.querySelector('[data-scrub-bar]');
      let current = -1;
      let pending = false;
      let preloading = false;
      let cssWidth = 0;
      let cssHeight = 0;

      section.classList.add('scrub--live');

      function frameUrl(i) {
        return template.replace('001', String(i + 1).padStart(3, '0'));
      }

      function loadFrame(i, done) {
        if (frames[i]) { if (done) done(); return; }
        const img = new Image();
        img.decoding = 'async';
        img.onload = function () { frames[i] = img; if (done) done(); };
        img.onerror = function () { if (done) done(); };
        img.src = frameUrl(i);
      }

      function preloadAll() {
        if (preloading) return;
        preloading = true;
        const order = [];
        const seen = {};
        [8, 4, 2, 1].forEach(function (step) {
          for (let i = 0; i < count; i += step) {
            if (!seen[i]) { seen[i] = true; order.push(i); }
          }
        });
        let cursor = 0;
        function next() {
          if (cursor >= order.length) return;
          const i = order[cursor++];
          loadFrame(i, function () { request(); next(); });
        }
        // Six in flight keeps the CDN busy without starving the page.
        for (let k = 0; k < 6; k++) next();
      }

      function nearestLoaded(i) {
        if (frames[i]) return frames[i];
        for (let d = 1; d < count; d++) {
          if (i - d >= 0 && frames[i - d]) return frames[i - d];
          if (i + d < count && frames[i + d]) return frames[i + d];
        }
        return null;
      }

      function draw(img) {
        const cw = canvas.width;
        const ch = canvas.height;
        // object-fit: cover, centred, so the portrait crop on phones keeps
        // the canister in frame.
        const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
        const w = img.naturalWidth * scale;
        const h = img.naturalHeight * scale;
        ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
      }

      function size() {
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        cssWidth = rect.width;
        cssHeight = rect.height;
        canvas.width = Math.round(rect.width * dpr);
        canvas.height = Math.round(rect.height * dpr);
        current = -1;
        render();
      }

      function progress() {
        const rect = section.getBoundingClientRect();
        const runway = section.offsetHeight - window.innerHeight;
        if (runway <= 0) return 0;
        return Math.min(1, Math.max(0, -rect.top / runway));
      }

      // The runway has three acts: an entry where the plate settles into place
      // (frame 1 held), the open itself, and a hold on the fully open canister
      // before the section releases. Frames, beats and the bar all run on the
      // middle act so the eye gets a beat of rest at either end.
      const ENTER = 0.10;
      const HOLD = 0.88;

      function render() {
        pending = false;
        if (!canvas.width) return;
        const raw = progress();
        const enter = Math.min(1, raw / ENTER);
        section.style.setProperty('--scrub-enter', enter.toFixed(3));
        const p = Math.min(1, Math.max(0, (raw - ENTER) / (HOLD - ENTER)));
        const index = Math.round(p * (count - 1));
        if (index !== current) {
          const img = nearestLoaded(index);
          if (img) { draw(img); current = index; }
        }
        beats.forEach(function (beat) {
          const active = p >= beat.from && p < beat.to;
          beat.el.classList.toggle('is-active', active);
        });
        section.classList.toggle('scrub--end', p >= 0.98);
        if (timeLabel) timeLabel.textContent = 'FRAME ' + String(index + 1).padStart(3, '0') + ' / ' + count;
        if (bar) bar.style.transform = 'scaleX(' + p + ')';
      }

      function request() {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(render);
      }

      // First frame straight away so the pinned block is never blank, then the
      // rest once the section is within a viewport of the fold.
      loadFrame(0, function () { size(); });
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            preloadAll();
          });
        }, { rootMargin: '100% 0px' });
        io.observe(section);
      } else {
        preloadAll();
      }

      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', function () {
        const rect = canvas.getBoundingClientRect();
        if (Math.abs(rect.width - cssWidth) > 1 || Math.abs(rect.height - cssHeight) > 1) size();
      }, { passive: true });
      reducedMotion.addEventListener && reducedMotion.addEventListener('change', function (event) {
        // Respect a mid-session switch to reduced motion: drop to the static layout.
        if (event.matches) section.classList.remove('scrub--live');
      });
    });
  }

  /* ---------- Homepage video: lazy-load + controls ---------- */
  function initHomeVideo() {
    document.querySelectorAll('[data-home-video]').forEach(function (video) {
      if (!claim(video, 'video')) return;

      const frame = video.closest('.home-video__frame');
      const toggle = frame ? frame.querySelector('[data-video-toggle]') : null;
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const lazySrc = video.getAttribute('data-video-src');
      let sourceLoaded = false;

      function loadSource() {
        if (sourceLoaded || !lazySrc) return;
        sourceLoaded = true;
        video.src = lazySrc;
        video.load();
      }

      const pauseLabel = toggle ? (toggle.getAttribute('data-pause-label') || 'Pause video') : 'Pause video';
      const playLabel = toggle ? (toggle.getAttribute('data-play-label') || 'Play video') : 'Play video';

      function syncVideoButton() {
        if (!toggle) return;
        const isPaused = video.paused;
        toggle.setAttribute('aria-pressed', String(isPaused));
        toggle.setAttribute('aria-label', isPaused ? playLabel : pauseLabel);
        toggle.textContent = isPaused ? 'Play' : 'Pause';
      }

      // Defer downloading the (large) video file until the section is near the viewport.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            loadSource();
            if (!reducedMotionQuery.matches) {
              video.play().catch(function () {});
            }
            syncVideoButton();
          });
        }, { rootMargin: '200px 0px' });
        io.observe(video);
      } else {
        loadSource();
        if (!reducedMotionQuery.matches) {
          video.play().catch(function () {});
        }
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          loadSource();
          if (video.paused) {
            video.play().catch(function () {});
          } else {
            video.pause();
          }
          syncVideoButton();
        });
        video.addEventListener('play', syncVideoButton);
        video.addEventListener('pause', syncVideoButton);
        if (reducedMotionQuery.addEventListener) {
          reducedMotionQuery.addEventListener('change', function () {
            if (reducedMotionQuery.matches) {
              video.pause();
              syncVideoButton();
            }
          });
        }
        syncVideoButton();
      }
    });
  }

  /* ---------- Film lightbox: one player, opened from any [data-film-open] ---------- */
  let filmPlayer = null;

  function formatFilmTime(seconds) {
    const whole = Math.max(0, Math.floor(seconds || 0));
    const minutes = Math.floor(whole / 60);
    const rest = whole % 60;
    return minutes + ':' + (rest < 10 ? '0' : '') + rest;
  }

  function bindFilmPlayer(dialog) {
    const video = dialog.querySelector('[data-film-video]');
    if (!video) return null;
    const stage = dialog.querySelector('[data-film-stage]');
    const tap = dialog.querySelector('[data-film-toggle]');
    const tapLabel = dialog.querySelector('[data-film-tap-label]');
    const soundButton = dialog.querySelector('[data-film-sound]');
    const fullscreenButton = dialog.querySelector('[data-film-fullscreen]');
    const closeButton = dialog.querySelector('[data-film-close]');
    const replayButton = dialog.querySelector('[data-film-replay]');
    const time = dialog.querySelector('[data-film-time]');
    const progress = dialog.querySelector('[data-film-progress]');
    let lastTrigger = null;
    let warmed = false;
    const inlineControls = video.controls;
    let nativeFullscreen = false;
    let wantsNativeFullscreen = false;
    let fullscreenPending = false;
    let fullscreenTimer = null;
    let fullscreenRequest = 0;

    function isNativeFullscreen() {
      return nativeFullscreen || video.webkitDisplayingFullscreen ||
        document.fullscreenElement === video || document.webkitFullscreenElement === video;
    }

    function syncFullscreen() {
      if (!fullscreenButton) return;
      const active = isNativeFullscreen();
      const expanded = dialog.classList.contains('is-expanded');
      fullscreenButton.textContent = fullscreenButton.getAttribute(active || expanded ? 'data-fullscreen-exit' : 'data-fullscreen-enter');
      fullscreenButton.disabled = fullscreenPending || video.readyState < 1;
      if (active) video.controls = true;
      else if (!fullscreenPending) video.controls = inlineControls;
    }

    function exitNativeFullscreen() {
      try {
        let result;
        if (video.webkitDisplayingFullscreen && typeof video.webkitExitFullscreen === 'function') {
          result = video.webkitExitFullscreen();
        } else if (document.fullscreenElement === video && document.exitFullscreen) {
          result = document.exitFullscreen();
        } else if (document.webkitFullscreenElement === video && document.webkitExitFullscreen) {
          result = document.webkitExitFullscreen();
        }
        if (result && result.catch) result.catch(function () {});
      } catch (error) {}
    }

    function settleFullscreen() {
      window.clearTimeout(fullscreenTimer);
      fullscreenPending = false;
      if (isNativeFullscreen() && (!dialog.open || !wantsNativeFullscreen)) exitNativeFullscreen();
      syncFullscreen();
    }

    function expandFilm() {
      if (!dialog.open || fullscreenPending || video.readyState < 1) return;
      if (isNativeFullscreen()) {
        wantsNativeFullscreen = false;
        exitNativeFullscreen();
        return;
      }
      if (dialog.classList.contains('is-expanded')) {
        dialog.classList.remove('is-expanded');
        syncFullscreen();
        return;
      }
      const request = ++fullscreenRequest;
      function fallback() {
        if (request !== fullscreenRequest) {
          if (!wantsNativeFullscreen) settleFullscreen();
          return;
        }
        wantsNativeFullscreen = false;
        settleFullscreen();
        if (dialog.open && !isNativeFullscreen()) dialog.classList.add('is-expanded');
        syncFullscreen();
      }
      fullscreenPending = true;
      wantsNativeFullscreen = true;
      video.controls = true;
      syncFullscreen();
      try {
        if (typeof video.webkitEnterFullscreen === 'function' && video.webkitSupportsFullscreen !== false) {
          fullscreenTimer = window.setTimeout(function () {
            if (request !== fullscreenRequest) {
              if (!wantsNativeFullscreen) settleFullscreen();
              return;
            }
            if (isNativeFullscreen()) settleFullscreen();
            else fallback();
          }, 1500);
          video.webkitEnterFullscreen();
        } else if (typeof video.requestFullscreen === 'function') {
          video.requestFullscreen().then(function () {
            if (request !== fullscreenRequest) {
              if (!wantsNativeFullscreen) settleFullscreen();
              return;
            }
            settleFullscreen();
          }, fallback);
        } else {
          fallback();
        }
      } catch (error) {
        fallback();
      }
    }

    if (fullscreenButton) fullscreenButton.addEventListener('click', expandFilm);
    video.addEventListener('webkitbeginfullscreen', function () {
      nativeFullscreen = true;
      settleFullscreen();
    });
    video.addEventListener('webkitendfullscreen', function () {
      nativeFullscreen = false;
      wantsNativeFullscreen = false;
      settleFullscreen();
      if (dialog.open && fullscreenButton) fullscreenButton.focus({ preventScroll: true });
    });
    video.addEventListener('loadedmetadata', syncFullscreen);
    function onFullscreenChange(event) {
      if (event.target !== video) return;
      if (!isNativeFullscreen()) wantsNativeFullscreen = false;
      settleFullscreen();
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    dialog.addEventListener('cancel', function (event) {
      if (dialog.classList.contains('is-expanded')) {
        event.preventDefault();
        dialog.classList.remove('is-expanded');
        syncFullscreen();
      }
    });

    // The film fades to black after its end card. Once it has ended, park on
    // the end card (data-film-end-card, seconds) so "Made to be kept." stays up
    // under REPLAY / VIEW OBJECT instead of an empty frame. `finished` holds
    // that state because seeking back clears video.ended.
    const endCardTime = parseFloat(video.getAttribute('data-film-end-card')) || 0;
    let finished = false;

    function play() {
      finished = false;
      return video.play().catch(function () {
        // Sound was refused (no gesture in this browser's eyes); play silent
        // rather than not at all, and let the button say so.
        if (!video.muted) {
          video.muted = true;
          return video.play().catch(function () {});
        }
      });
    }

    function sync() {
      const ended = finished;
      const paused = video.paused && !ended;
      dialog.classList.toggle('is-paused', paused);
      dialog.classList.toggle('is-ended', ended);
      if (tap) tap.setAttribute('aria-label', ended ? 'Replay film' : (paused ? 'Play film' : 'Pause film'));
      if (tapLabel) tapLabel.textContent = 'Play';
      if (soundButton) soundButton.textContent = video.muted ? 'Sound on' : 'Sound off';
      const total = isFinite(video.duration) && video.duration > 0 ? video.duration : 48;
      // Parked on the end card after finishing, the readout still says "done".
      const position = finished ? total : video.currentTime;
      if (time) time.textContent = formatFilmTime(position) + ' / ' + formatFilmTime(total);
      if (progress) progress.style.width = Math.min(100, (position / total) * 100) + '%';
    }

    // Phones held upright get the 9:16 cut (first <source> in the snippet). The
    // frame starts tall when that source should win and then follows the video
    // that actually loaded, so a browser that ignores <source media> and plays
    // the 16:9 file still gets a 16:9 frame.
    const portraitFilm = window.matchMedia('(max-width: 819px) and (orientation: portrait)');

    function syncFrameShape() {
      const known = video.videoWidth > 0 && video.videoHeight > 0;
      const tall = known ? video.videoHeight > video.videoWidth : portraitFilm.matches;
      dialog.classList.toggle('is-portrait', tall);
    }
    video.addEventListener('loadedmetadata', syncFrameShape);

    function warm() {
      // A hover or focus on any trigger is a strong hint: start buffering so the
      // film opens without a spinner. Nothing downloads before that.
      if (warmed) return;
      warmed = true;
      const poster = (portraitFilm.matches && video.getAttribute('data-film-poster-portrait')) || video.getAttribute('data-film-poster');
      if (!video.poster && poster) video.poster = poster;
      syncFrameShape();
      video.preload = 'auto';
      video.load();
    }

    function open(trigger) {
      if (dialog.open) return;
      lastTrigger = trigger || null;
      warm();
      dialog.showModal();
      syncOverlayChrome();
      video.muted = false;
      video.volume = 1;
      try { video.currentTime = 0; } catch (error) { /* not seekable yet; starts at 0 anyway */ }
      play().then(sync, sync);
      sync();
      syncFullscreen();
      // Focus the player itself, not the frame button: Safari treats
      // script-moved focus as keyboard focus, which lit the PLAY label over a
      // playing film. Space still toggles from here; Tab reaches the controls.
      dialog.focus({ preventScroll: true });
    }

    function toggle() {
      if (finished || video.ended) {
        try { video.currentTime = 0; } catch (error) {}
        play();
      } else if (video.paused) {
        play();
      } else {
        video.pause();
      }
    }

    video.addEventListener('ended', function () {
      finished = true;
      if (endCardTime > 0 && video.seekable.length && video.seekable.end(video.seekable.length - 1) >= endCardTime) {
        try { video.currentTime = endCardTime; } catch (error) { /* stays on the last frame */ }
      }
    });
    ['play', 'pause', 'ended', 'timeupdate', 'volumechange', 'loadedmetadata', 'seeked'].forEach(function (type) {
      video.addEventListener(type, sync);
    });

    if (tap) tap.addEventListener('click', toggle);
    if (replayButton) {
      replayButton.addEventListener('click', function () {
        try { video.currentTime = 0; } catch (error) {}
        play();
      });
    }
    if (soundButton) {
      soundButton.addEventListener('click', function () {
        video.muted = !video.muted;
        if (!video.muted) video.volume = 1;
        sync();
      });
    }
    if (closeButton) closeButton.addEventListener('click', function () { dialog.close(); });

    // Click on the dark stage outside the frame (or the dialog's own padding) closes.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target === stage) dialog.close();
    });

    dialog.addEventListener('keydown', function (event) {
      const onControl = event.target && event.target.closest('button, a, input');
      if (event.key === ' ' && (!onControl || event.target === tap)) {
        event.preventDefault();
        toggle();
      } else if ((event.key === 'm' || event.key === 'M') && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        video.muted = !video.muted;
        sync();
      }
    });

    dialog.addEventListener('close', function () {
      fullscreenRequest += 1;
      wantsNativeFullscreen = false;
      dialog.classList.remove('is-expanded');
      exitNativeFullscreen();
      video.controls = inlineControls;
      syncFullscreen();
      video.pause();
      syncOverlayChrome();
      if (lastTrigger && lastTrigger.isConnected) lastTrigger.focus({ preventScroll: true });
    });

    // Hidden tab: hold the film rather than let the score play on unseen.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && dialog.open && !video.paused) video.pause();
    });

    sync();
    return { open: open, warm: warm };
  }

  function initFilmLightbox() {
    const dialog = document.querySelector('[data-film-lightbox]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    if (claim(dialog, 'film')) filmPlayer = bindFilmPlayer(dialog);
    if (!filmPlayer) return;

    document.querySelectorAll('[data-film-open]').forEach(function (trigger) {
      if (!claim(trigger, 'film-open')) return;
      trigger.addEventListener('click', function () { filmPlayer.open(trigger); });
      trigger.addEventListener('pointerenter', filmPlayer.warm, { once: true });
      trigger.addEventListener('focus', filmPlayer.warm, { once: true });
    });
  }

  /* ---------- Quantity steppers (product form AND cart lines) ---------- */
  function initQty() {
    document.querySelectorAll('[data-qty]').forEach(function (qty) {
      if (!claim(qty, 'qty')) return;

      const input = qty.querySelector('input');
      // Everything below writes input.value, so bail once here rather than
      // guarding each read with `input?.` and then assigning unguarded.
      if (!input) return;
      const minus = qty.querySelector('[data-qty-minus]');
      const plus = qty.querySelector('[data-qty-plus]');

      function maxQty() {
        const m = parseInt(input.getAttribute('max') || '', 10);
        return Number.isFinite(m) && m > 0 ? m : Infinity;
      }

      function currentQty() {
        return Math.min(maxQty(), Math.max(1, parseInt(input.value || '1', 10) || 1));
      }

      function updateQtyState() {
        const v = currentQty();
        input.value = v;
        if (minus) minus.disabled = v <= 1;
        if (plus) plus.disabled = v >= maxQty();
      }

      if (minus) {
        minus.addEventListener('click', function () {
          input.value = Math.max(1, currentQty() - 1);
          updateQtyState();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      if (plus) {
        plus.addEventListener('click', function () {
          input.value = Math.min(maxQty(), currentQty() + 1);
          updateQtyState();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      input.addEventListener('input', updateQtyState);
      input.addEventListener('change', updateQtyState);
      updateQtyState();
    });
  }

  /* ---------- Product gallery, swipe navigation and full-screen detail ---------- */
  let galleryState = null;
  let galleryKeysBound = false;

  function galleryImage(item) {
    return {
      src: item.getAttribute('data-src') || item.getAttribute('src'),
      fullSrc: item.getAttribute('data-full-src') || item.getAttribute('data-src') || item.getAttribute('src'),
      srcset: item.getAttribute('data-srcset') || item.getAttribute('srcset'),
      sizes: item.getAttribute('data-sizes') || item.getAttribute('sizes'),
      alt: item.getAttribute('data-alt') || item.getAttribute('alt') || '',
      width: item.getAttribute('data-width') || item.getAttribute('width'),
      height: item.getAttribute('data-height') || item.getAttribute('height')
    };
  }

  // Warm only adjacent display-sized images; zoom originals stay on demand.
  function warmGalleryImage(state, index) {
    const connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType))) return;
    const count = state.items.length;
    index = ((index % count) + count) % count;
    if (index === state.index || state.preloaded.has(index)) return;
    const source = galleryImage(state.items[index]);
    const image = new Image();
    state.preloaded.set(index, image);
    image.decoding = 'async';
    image.fetchPriority = 'low';
    if (source.sizes) image.sizes = source.sizes;
    if (source.srcset) image.srcset = source.srcset;
    image.src = source.src;
    if (image.decode) image.decode().catch(function () {});
  }

  function warmGalleryNeighbors(state) {
    if (galleryState !== state || !state.gallery.isConnected) return;
    warmGalleryImage(state, state.index + 1);
    warmGalleryImage(state, state.index - 1);
  }

  function activateThumb(index, moveFocus) {
    if (!galleryState) return;
    const state = galleryState;
    const thumbs = state.thumbs;
    const main = state.main;
    const count = state.items.length;
    index = ((index % count) + count) % count;
    const source = galleryImage(state.items[index]);

    if (source.src) {
      // Preserve the main image's responsive contract when selecting a photo.
      // Only the dialog fetches the original image for close inspection.
      if (source.sizes) main.setAttribute('sizes', source.sizes); else main.removeAttribute('sizes');
      if (source.srcset) main.setAttribute('srcset', source.srcset); else main.removeAttribute('srcset');
      main.src = source.src;
      main.alt = source.alt;
      if (source.width && source.height) {
        main.setAttribute('width', source.width);
        main.setAttribute('height', source.height);
      }
    }

    thumbs.forEach(function (t, i) {
      const isActive = i === index;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
    state.index = index;
    const thumb = thumbs[index];
    // Scroll just the thumbnail rail; scrollIntoView would also jump the page.
    if (thumb && state.rail && state.rail.scrollWidth > state.rail.clientWidth) {
      const railRect = state.rail.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      if (thumbRect.left < railRect.left) state.rail.scrollLeft += thumbRect.left - railRect.left;
      else if (thumbRect.right > railRect.right) state.rail.scrollLeft += thumbRect.right - railRect.right;
    }
    if (moveFocus) (thumb || state.opener).focus({ preventScroll: true });
    if (state.dialog && state.dialog.open) updateLightbox(state);
    warmGalleryNeighbors(state);
  }

  function galleryInView(gallery) {
    const r = gallery.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > 0 && r.top < vh;
  }

  function onGalleryKey(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if (!galleryState) return;
    if (galleryState.dialog && galleryState.dialog.open) {
      // A focused, zoomed canvas uses arrow keys for native panning instead.
      if (galleryState.zoomed && document.activeElement === galleryState.stage) return;
      event.preventDefault();
      activateThumb(galleryState.index + (event.key === 'ArrowRight' ? 1 : -1), false);
      return;
    }
    // An open overlay owns the keyboard. Swapping the gallery behind it would
    // be invisible, and Escape/Tab handling belongs to the overlay.
    if (document.querySelector(OVERLAY_SELECTOR)) return;

    const active = document.activeElement;
    // Never swallow arrow keys inside a field: they are caret movement there.
    if (active && (active.matches('input, textarea, select') || active.isContentEditable)) return;

    const focusInGallery = galleryState.viewer.contains(active);
    // Claim the key only when the gallery really is the target: focus inside
    // it, or nothing else focused and the gallery on screen. Otherwise leave
    // the key to whatever the customer is actually using.
    const idle = !active || active === document.body || active === document.documentElement;
    if (!focusInGallery && !(idle && galleryInView(galleryState.viewer))) return;

    event.preventDefault();
    activateThumb(galleryState.index + (event.key === 'ArrowRight' ? 1 : -1), focusInGallery);
  }

  // Native pan-y keeps ordinary page scrolling and pinch-to-zoom available.
  // A horizontal gesture changes one image, and its trailing click is ignored.
  function bindGallerySwipe(surface, state, canSwipe) {
    let start = null;
    let moved = false;
    let suppressUntil = 0;
    surface.addEventListener('pointerdown', function (event) {
      if (!event.isPrimary || event.button !== 0) return;
      start = { x: event.clientX, y: event.clientY, id: event.pointerId };
      moved = false;
    });
    surface.addEventListener('pointermove', function (event) {
      if (!start || event.pointerId !== start.id) return;
      if (Math.abs(event.clientX - start.x) > 10 || Math.abs(event.clientY - start.y) > 10) moved = true;
    });
    surface.addEventListener('pointerup', function (event) {
      if (!start || event.pointerId !== start.id) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (moved || Math.abs(dx) > 10 || Math.abs(dy) > 10) suppressUntil = Date.now() + 500;
      if (canSwipe() && Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        activateThumb(state.index + (dx < 0 ? 1 : -1), false);
      }
      start = null;
    });
    surface.addEventListener('pointercancel', function () { start = null; suppressUntil = Date.now() + 500; });
    surface.addEventListener('click', function (event) {
      if (event.detail !== 0 && Date.now() < suppressUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  }

  function sizeLightboxImage(state, center) {
    const image = state.fullImage;
    if (!image.naturalWidth || !image.naturalHeight || !state.dialog.open) return;
    const width = state.stage.clientWidth;
    const height = state.stage.clientHeight;
    const fit = Math.min(width / image.naturalWidth, height / image.naturalHeight, 1);
    // The original asset supplies detail; zoom enlarges its rendered dimensions
    // inside a scrollable canvas instead of cropping an object-fit image.
    const scale = state.zoomed ? Math.max(fit * 2, 1) : fit;
    const imageWidth = Math.round(image.naturalWidth * scale);
    const imageHeight = Math.round(image.naturalHeight * scale);
    image.style.width = imageWidth + 'px';
    image.style.height = imageHeight + 'px';
    state.canvas.style.width = Math.max(width, imageWidth) + 'px';
    state.canvas.style.height = Math.max(height, imageHeight) + 'px';
    if (center) {
      state.stage.scrollLeft = (imageWidth - width) / 2;
      state.stage.scrollTop = (imageHeight - height) / 2;
    }
  }

  function setLightboxZoom(state, zoomed) {
    state.zoomed = zoomed;
    state.stage.classList.toggle('is-zoomed', zoomed);
    state.zoomButton.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
    state.zoomButton.textContent = zoomed ? 'Zoom out' : 'Zoom in';
    state.help.textContent = zoomed ? 'Scroll to explore the image. Tap the image or select Zoom out to see it in full.' : 'Swipe to browse. Tap the image or select Zoom in for detail.';
    sizeLightboxImage(state, true);
  }

  function updateLightbox(state) {
    const source = galleryImage(state.items[state.index]);
    setLightboxZoom(state, false);
    state.fullImage.style.visibility = 'hidden';
    // Keep keyboard focus inside the viewer when navigating from the zoom
    // control, which is disabled while the next full image loads.
    if (document.activeElement === state.zoomButton) state.stage.focus({ preventScroll: true });
    state.zoomButton.disabled = true;
    state.fullImage.alt = source.alt;
    state.caption.textContent = source.alt;
    state.counter.textContent = (state.index + 1) + ' / ' + state.items.length;
    state.fullImage.src = source.fullSrc;
  }

  function initLightbox(state) {
    const dialog = state.dialog;
    if (!dialog || typeof dialog.showModal !== 'function' || !state.opener) return;
    state.stage = dialog.querySelector('[data-gallery-stage]');
    state.canvas = dialog.querySelector('[data-gallery-canvas]');
    state.fullImage = dialog.querySelector('[data-gallery-full-image]');
    state.caption = dialog.querySelector('[data-gallery-caption]');
    state.counter = dialog.querySelector('[data-gallery-count]');
    state.zoomButton = dialog.querySelector('[data-gallery-zoom]');
    state.help = dialog.querySelector('[data-gallery-help]');
    state.opener.hidden = false;

    bindGallerySwipe(state.opener, state, function () { return state.items.length > 1; });
    bindGallerySwipe(state.stage, state, function () { return !state.zoomed && state.items.length > 1; });
    state.opener.addEventListener('click', function () {
      // Safari need not focus a pointer-activated button; always return to
      // the actual gallery trigger when the viewer closes.
      state.lastFocused = state.opener;
      dialog.showModal();
      updateLightbox(state);
      syncOverlayChrome();
    });
    dialog.querySelector('[data-gallery-close]').addEventListener('click', function () { dialog.close(); });
    dialog.addEventListener('close', function () {
      syncOverlayChrome();
      if (state.lastFocused && state.lastFocused.isConnected) state.lastFocused.focus({ preventScroll: true });
    });
    dialog.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach(function (button) {
      button.disabled = state.items.length < 2;
      button.addEventListener('click', function () {
        activateThumb(state.index + (button.hasAttribute('data-gallery-next') ? 1 : -1), false);
      });
    });
    state.zoomButton.addEventListener('click', function () { setLightboxZoom(state, !state.zoomed); });
    state.stage.addEventListener('click', function (event) {
      if (!state.zoomButton.disabled && event.target === state.fullImage) setLightboxZoom(state, !state.zoomed);
    });
    state.fullImage.addEventListener('load', function () {
      state.fullImage.width = state.fullImage.naturalWidth;
      state.fullImage.height = state.fullImage.naturalHeight;
      state.fullImage.style.visibility = '';
      state.zoomButton.disabled = false;
      sizeLightboxImage(state, true);
    });
    state.fullImage.addEventListener('error', function () {
      state.caption.textContent = 'This image could not load. Please try another image.';
      state.zoomButton.disabled = true;
    });
    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(function () { sizeLightboxImage(state, true); });
      state.resizeObserver.observe(state.stage);
    }
  }

  function initGallery() {
    const gallery = document.querySelector('[data-product-gallery]');
    if (galleryState && galleryState.gallery === gallery) return;
    if (galleryState) {
      if (galleryState.dialog && galleryState.dialog.open) galleryState.dialog.close();
      if (galleryState.resizeObserver) galleryState.resizeObserver.disconnect();
      syncOverlayChrome();
    }
    if (!gallery) {
      galleryState = null;
      return;
    }
    const main = gallery.querySelector('[data-gallery-main] img');
    const thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-thumb]'));
    if (!main) {
      galleryState = null;
      return;
    }

    let index = thumbs.findIndex(function (t) { return t.classList.contains('is-active'); });
    if (index < 0) index = 0;
    galleryState = {
      gallery: gallery,
      viewer: gallery.querySelector('.product__gallery'),
      main: main,
      thumbs: thumbs,
      items: thumbs.length ? thumbs : [main],
      rail: gallery.querySelector('.product__thumbs'),
      opener: gallery.querySelector('[data-gallery-open]'),
      dialog: gallery.querySelector('[data-gallery-dialog]'),
      index: index,
      preloaded: new Map(),
      zoomed: false
    };
    initLightbox(galleryState);
    const state = galleryState;
    function warmAfterMain() {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(function () { warmGalleryNeighbors(state); }, { timeout: 1500 });
      } else {
        window.setTimeout(function () { warmGalleryNeighbors(state); }, 250);
      }
    }
    if (main.complete) warmAfterMain();
    else main.addEventListener('load', warmAfterMain, { once: true });

    thumbs.forEach(function (thumb, i) {
      if (!claim(thumb, 'thumb')) return;
      thumb.addEventListener('click', function () { activateThumb(i, false); });
      thumb.addEventListener('pointerenter', function () { warmGalleryImage(state, i); });
      thumb.addEventListener('focus', function () { warmGalleryImage(state, i); });
    });

    gallery.querySelectorAll('[data-gallery-inline-prev], [data-gallery-inline-next]').forEach(function (button) {
      button.hidden = galleryState.items.length < 2;
      if (!claim(button, 'gallery-inline-arrow')) return;
      button.addEventListener('click', function () {
        activateThumb(galleryState.index + (button.hasAttribute('data-gallery-inline-next') ? 1 : -1), false);
      });
    });

    // Document-level, so bound once and pointed at whatever gallery is current.
    if (!galleryKeysBound) {
      galleryKeysBound = true;
      document.addEventListener('keydown', onGalleryKey);
    }
  }

  /* ---------- Quiet section reveal ---------- */
  function initReveal() {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: no-preference)');
    if (!motionQuery.matches || !('IntersectionObserver' in window)) return;

    const revealSelectors = [
      'body.template-index main > .shopify-section:not(:first-child)',
      'body.template-index main > section:not(.hero)',
      'body.template-collection .product-card',
      'body.template-blog .notes__item',
      'body.template-page .notes__item',
      'body.template-suffix-about main > .shopify-section:not(:first-child)'
    ].join(',');

    const revealItems = Array.prototype.slice.call(document.querySelectorAll(revealSelectors));
    if (!revealItems.length) return;

    const observer = new IntersectionObserver(function (entries, io) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.12
    });

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    revealItems.forEach(function (item, index) {
      if (!claim(item, 'reveal')) return;
      // theme.js is deferred, so this runs AFTER first paint. Adding the
      // opacity:0 class to something already on screen makes it flash out and
      // fade back in, so anything already in view is left alone entirely.
      if (item.getBoundingClientRect().top < viewportHeight) return;
      item.classList.add('motion-reveal');
      item.style.setProperty('--motion-delay', (Math.min(index % 4, 3) * 70) + 'ms');
      observer.observe(item);
    });
  }

  /* ---------- Variant select -> form variant id ---------- */
  function initVariantSelects() {
    document.querySelectorAll('[data-variant-select]').forEach(function (select) {
      if (!claim(select, 'variant')) return;
      select.addEventListener('change', function (e) {
        const form = e.target.closest('form');
        if (!form) return;
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.value = e.target.value;
      });
    });
  }

  /* ---------- Mobile sticky add to cart ---------- */
  const stickyMobileQuery = window.matchMedia('(max-width: 767px)');
  let stickyRefs = null;
  let stickyObserver = null;
  let stickyQueryBound = false;

  function syncStickyForm() {
    if (!stickyRefs) return;
    const bar = stickyRefs.bar;
    const form = stickyRefs.form;
    const variantSelect = form.querySelector('[data-variant-select]');
    const mainVariantInput = form.querySelector('input[name="id"]');
    const mainQtyInput = form.querySelector('input[name="quantity"]');
    const stickyVariantInput = bar.querySelector('[data-sticky-variant-id]');
    const stickyQtyInput = bar.querySelector('[data-sticky-quantity]');
    const variantId = (variantSelect && variantSelect.value) || (mainVariantInput && mainVariantInput.value);
    if (variantId && stickyVariantInput) stickyVariantInput.value = variantId;
    if (mainQtyInput && stickyQtyInput) {
      stickyQtyInput.value = Math.max(1, parseInt(mainQtyInput.value || '1', 10) || 1);
    }
  }

  function setStickyVisible(show) {
    if (!stickyRefs) return;
    stickyRefs.bar.classList.toggle('is-visible', show);
    stickyRefs.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  function syncStickyObserver() {
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }
    if (!stickyRefs) return;
    // base.css hides the bar above 767px, so there is nothing to show there.
    // The old scroll listener ran unthrottled on desktop anyway, measuring a
    // display:none element on every frame.
    if (!stickyMobileQuery.matches || !('IntersectionObserver' in window)) {
      setStickyVisible(false);
      return;
    }
    stickyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        syncStickyForm();
        // Show whenever the real form is off screen, above or below. On a phone
        // the main ADD TO CART often starts below the first screen, and the bar
        // gives a buyer the button from the first screen. It hides while the
        // real form is visible, so there are never two buttons in view.
        setStickyVisible(!entry.isIntersecting);
      });
    }, { threshold: 0 });
    stickyObserver.observe(stickyRefs.form);
  }

  // IntersectionObserver only reports transitions. A programmatic jump (anchor
  // link, back-to-top) can move the form from above the viewport to below it
  // without ever intersecting, leaving the bar showing over a form that is on
  // its way. scrollend fires once per scroll, so a single re-measure there is
  // cheap; browsers without it keep the observer-only behaviour.
  function settleStickyAfterScroll() {
    if (!stickyRefs || !stickyObserver) return;
    const rect = stickyRefs.form.getBoundingClientRect();
    setStickyVisible(rect.bottom <= 0 || rect.top >= window.innerHeight);
  }

  function initStickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    const form = document.querySelector('.product__form');
    stickyRefs = (bar && form) ? { bar: bar, form: form } : null;

    if (stickyRefs && claim(form, 'sticky')) {
      // Delegated on the form: change/input bubble, so the variant select and
      // the quantity field stay mirrored without binding each one.
      form.addEventListener('change', syncStickyForm);
      form.addEventListener('input', syncStickyForm);
    }
    if (!stickyQueryBound && stickyMobileQuery.addEventListener) {
      stickyQueryBound = true;
      stickyMobileQuery.addEventListener('change', syncStickyObserver);
      window.addEventListener('scrollend', settleStickyAfterScroll, { passive: true });
    }
    syncStickyForm();
    syncStickyObserver();
  }

  /* ---------- Homepage phone buy bar ----------
     Shown on phones once the hero has scrolled away, hidden while the hero or
     the buy section is on screen, so it never doubles up a visible button.
     Only phones get it: the styles live in the same max-width: 767px block as
     the product page bar. */
  function initHomeBuyBar() {
    const bar = document.querySelector('.shopify-section [data-home-buybar]') || document.querySelector('[data-home-buybar]');
    if (!bar || !claim(bar, 'home-buybar')) return;
    // The section wrapper animates in with a transform (motion-reveal), and a
    // transformed ancestor turns position: fixed into position: absolute.
    // Moving the bar to <body> keeps it pinned to the screen. A theme-editor
    // re-render brings a fresh copy, so drop any bar moved out earlier.
    document.querySelectorAll('body > [data-home-buybar]').forEach(function (old) {
      if (old !== bar) old.remove();
    });
    if (bar.parentElement !== document.body) document.body.appendChild(bar);
    const anchors = Array.prototype.slice.call(document.querySelectorAll('[data-home-buybar-hide]'));
    if (!anchors.length || !('IntersectionObserver' in window)) return;
    const onScreen = new Set();
    function apply() {
      const show = stickyMobileQuery.matches && onScreen.size === 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }
    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      });
      apply();
    }, { threshold: 0 });
    anchors.forEach(function (anchor) { observer.observe(anchor); });
    if (stickyMobileQuery.addEventListener) stickyMobileQuery.addEventListener('change', apply);
  }

  /* ---------- Per-form success notes ----------
     form.posted_successfully? is scoped to the form TYPE, not the instance, so
     a footer newsletter post used to light up the restock note and the
     CANISTER 120 interest note as well. Each form now carries its own
     return_to fragment; the note is rendered hidden and only the one the
     redirect actually names is revealed. Shopify appends ?customer_posted=true
     to return_to, which can land INSIDE the fragment, so match on the part
     before any query string. */
  function initFormNotes() {
    const notes = document.querySelectorAll('[data-form-note]');
    if (!notes.length) return;
    const raw = (window.location.hash || '').replace('#', '');
    const key = raw.split('?')[0].split('&')[0];
    const posted = /[?&]customer_posted=true/.test(window.location.search)
      || /customer_posted=true/.test(raw);
    if (key) {
      // Match either our own return_to fragment or the form's id: Shopify's
      // stock redirect uses the form id as the fragment, so if return_to's
      // fragment is ever dropped the note still finds its form.
      notes.forEach(function (note) {
        const form = note.closest('form');
        if (note.getAttribute('data-form-note') === key || (form && form.id === key)) {
          note.hidden = false;
        }
      });
      return;
    }
    // No fragment survived but the post succeeded: only safe to reveal when
    // exactly one customer form is on the page, otherwise we would be back to
    // lighting up every note at once.
    if (posted && notes.length === 1) notes[0].hidden = false;
  }

  /* ---------- Cart page quantity updates ----------
     assets/cart-drawer.js is deliberately not loaded on /cart, so the cart
     page's own ajax lives here. Every stepper click used to call form.submit(),
     a full reload that threw away scroll position and focus. */
  let cartPageSectionId = null;
  let cartPageTimer = null;
  let cartPagePendingFocus = null;

  function syncCartCountText(count) {
    // Visible text is the accessible name here (WCAG 2.5.3 Label in Name), so
    // do not paint an aria-label over it.
    document.querySelectorAll('[data-cart-count-text]').forEach(function (el) {
      el.textContent = 'CART (' + count + ')';
    });
  }

  function cartPageWrapper() {
    return cartPageSectionId ? document.getElementById('shopify-section-' + cartPageSectionId) : null;
  }

  function nativeCartSubmit() {
    // The native cart post sets ABSOLUTE quantities, so replaying it after an
    // unknown outcome cannot double a line the way a relative change would.
    const form = document.querySelector('[data-cartx-form]');
    if (form) form.submit();
  }

  function restoreCartFocus() {
    if (!cartPagePendingFocus) return;
    const pending = cartPagePendingFocus;
    cartPagePendingFocus = null;
    const scope = document.querySelector('[data-cart-line-key="' + pending.key + '"]');
    if (!scope) return;
    let target = null;
    if (pending.role === 'minus') target = scope.querySelector('[data-qty-minus]');
    else if (pending.role === 'plus') target = scope.querySelector('[data-qty-plus]');
    // The button that was used may have just become disabled at the new
    // quantity, and focus() is a no-op there; fall back to the field.
    if (!target || target.disabled) target = scope.querySelector('input');
    if (target) target.focus();
  }

  async function renderCartSection() {
    const wrapper = cartPageWrapper();
    if (!wrapper) throw new Error('cart section missing');
    // No Accept: application/json here. On /cart that header makes Shopify
    // answer with the cart object (the /cart.js behaviour) instead of the
    // sections payload, so the section key is never present.
    const response = await fetch(ROOT + 'cart?sections=' + encodeURIComponent(cartPageSectionId), {
      signal: timeoutSignal()
    });
    if (!response.ok) throw new Error('section render failed');
    const payload = await response.json();
    const markup = payload[cartPageSectionId];
    if (!markup) throw new Error('section missing');
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const next = parsed.getElementById(wrapper.id);
    if (!next) throw new Error('content missing');
    wrapper.innerHTML = next.innerHTML;
    // The steppers inside were just replaced and have no listeners.
    initQty();
  }

  async function applyCartQuantity(key, quantity) {
    const response = await fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ id: key, quantity: quantity }),
      signal: timeoutSignal()
    });
    if (!response.ok) throw new Error('cart change failed');
    const cart = await response.json();
    syncCartCountText(cart.item_count);
    await renderCartSection();
    restoreCartFocus();
  }

  function queueCartQuantity(wrap, role) {
    const input = wrap.querySelector('input');
    const key = wrap.getAttribute('data-cart-line-key');
    if (!input || !key) return;
    // Post the stable line-item key, never the positional /cart/change?line=N.
    cartPagePendingFocus = { key: key, role: role };
    window.clearTimeout(cartPageTimer);
    cartPageTimer = window.setTimeout(function () {
      const quantity = Math.max(1, parseInt(input.value || '1', 10) || 1);
      applyCartQuantity(key, quantity).catch(nativeCartSubmit);
    }, 500);
  }

  function initCartPage() {
    const cartSection = document.querySelector('[data-cart-section-id]');
    if (!cartSection) return;
    cartPageSectionId = cartSection.getAttribute('data-cart-section-id');
    const wrapper = cartPageWrapper();
    // Listeners live on the section WRAPPER, the one node that survives every
    // re-render; the form and the lines inside it do not.
    if (!wrapper || !claim(wrapper, 'cartpage')) return;

    wrapper.addEventListener('click', function (event) {
      const button = event.target.closest('[data-qty-minus], [data-qty-plus]');
      if (!button) return;
      const wrap = button.closest('[data-cart-line-key]');
      if (wrap) queueCartQuantity(wrap, button.hasAttribute('data-qty-plus') ? 'plus' : 'minus');
    });

    wrapper.addEventListener('change', function (event) {
      const input = event.target;
      if (!input.matches || !input.matches('input[name="updates[]"]')) return;
      const wrap = input.closest('[data-cart-line-key]');
      if (wrap) queueCartQuantity(wrap, 'input');
    });

    wrapper.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      const input = event.target;
      if (!input.matches || !input.matches('input[name="updates[]"]')) return;
      // Enter in a number field would submit the cart form and reload the page.
      event.preventDefault();
      const wrap = input.closest('[data-cart-line-key]');
      if (wrap) queueCartQuantity(wrap, 'input');
    });
  }

  /* ---------- Cookie banner ---------- */
  function initCookieBanner() {
    const banner = document.querySelector('[data-cookie-banner]');
    if (!banner || !claim(banner, 'cookie')) return;

    const acceptButton = banner.querySelector('[data-cookie-accept]');
    const manageButton = banner.querySelector('[data-cookie-manage]');
    const declineButton = banner.querySelector('[data-cookie-decline]');
    const saveButton = banner.querySelector('[data-cookie-save]');
    const preferencesPanel = banner.querySelector('[data-cookie-preferences]');
    const storageKey = 'archive135_cookie_consent_v1';
    const dismissalKey = 'archive135_cookie_service_dismissed';
    const description = banner.querySelector('[data-cookie-description]');
    const actions = banner.querySelector('[data-cookie-actions]');
    const status = banner.querySelector('[data-cookie-status]');
    const recovery = banner.querySelector('[data-cookie-recovery]');
    const retryButton = banner.querySelector('[data-cookie-retry]');
    const closeButton = banner.querySelector('[data-cookie-close]');
    const purposes = ['analytics', 'preferences', 'marketing'];
    let nativeObserver = null;
    let nativeWatchTimer;
    let customInteraction = false;
    let nativeUsedInTab = false;
    let saving = false;
    let lastAttempt = null;
    let returnFocus = null;
    let dismissed = false;
    try {
      const state = window.sessionStorage.getItem(dismissalKey);
      dismissed = state === '1';
      nativeUsedInTab = state === 'native';
    } catch (_error) {}

    function stopWatchingNative() {
      if (nativeObserver) nativeObserver.disconnect();
      window.clearTimeout(nativeWatchTimer);
      document.removeEventListener('load', handOffToNative, true);
    }

    function useCustomControls() {
      customInteraction = true;
      stopWatchingNative();
    }

    function handOffToNative() {
      if (customInteraction || !window.privacyBanner?.showPreferences) return false;
      const native = document.getElementById('shopify-pc__banner');
      const ready = native?.querySelector('#shopify-pc__banner__btn-accept') &&
        native.querySelector('#shopify-pc__banner__btn-decline');
      if (!ready && !nativeUsedInTab) return false;
      document.documentElement.setAttribute('data-cookie-native', '');
      if (!nativeUsedInTab && (!native.getClientRects().length || getComputedStyle(native).visibility !== 'visible')) {
        document.documentElement.removeAttribute('data-cookie-native');
        return false;
      }
      banner.hidden = true;
      stopWatchingNative();
      try { window.sessionStorage.setItem(dismissalKey, 'native'); } catch (_error) {}
      return true;
    }

    function handleLoadFailure() {
      if (handOffToNative()) return;
      refreshBanner();
      if (customInteraction || nativeObserver) return;
      nativeObserver = new MutationObserver(handOffToNative);
      nativeObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'hidden'] });
      document.addEventListener('load', handOffToNative, true);
      nativeWatchTimer = window.setTimeout(stopWatchingNative, 10000);
    }

    function setDismissed(value) {
      dismissed = value;
      try {
        if (value) window.sessionStorage.setItem(dismissalKey, '1');
        else window.sessionStorage.removeItem(dismissalKey);
      } catch (_error) {}
    }

    function withDeadline(start) {
      return new Promise(function (resolve, reject) {
        const timer = window.setTimeout(reject, 4000);
        function finish(failed, value) {
          window.clearTimeout(timer);
          if (failed) reject();
          else resolve(value);
        }
        try { start(finish); } catch (_error) { finish(true); }
      });
    }

    function getCustomerPrivacy() {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return privacy && typeof privacy.setTrackingConsent === 'function' &&
        typeof privacy.currentVisitorConsent === 'function' && typeof privacy.shouldShowBanner === 'function'
        ? privacy : null;
    }

    function loadCustomerPrivacy() {
      return withDeadline(function (finish) {
        if (getCustomerPrivacy()) return finish(false, getCustomerPrivacy());
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return finish(true);
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], function (error) {
          const privacy = getCustomerPrivacy();
          finish(error || !privacy, privacy);
        });
      });
    }

    function refreshBanner() {
      if (document.documentElement.hasAttribute('data-cookie-native') || saving || lastAttempt || returnFocus) return;
      try {
        const privacy = getCustomerPrivacy();
        const consent = privacy.currentVisitorConsent();
        const hasAnswered = purposes.every(function (purpose) {
          return consent[purpose] === 'yes' || consent[purpose] === 'no';
        });
        banner.hidden = !privacy.shouldShowBanner() || hasAnswered;
      } catch (_error) {
        banner.hidden = dismissed;
      }
    }

    function storeChoice(choice, consent) {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify({
          choice: choice,
          consent: consent,
          updatedAt: new Date().toISOString()
        }));
      } catch (_error) {
        // Consent still submits through Shopify when local storage is unavailable.
      }
    }

    function resetControls() {
      description.hidden = false;
      actions.hidden = false;
      preferencesPanel.hidden = true;
      manageButton.setAttribute('aria-expanded', 'false');
      recovery.hidden = true;
      status.textContent = '';
      lastAttempt = null;
    }

    function hideBanner() {
      const hadFocus = banner.contains(document.activeElement);
      banner.hidden = true;
      resetControls();
      if (hadFocus) {
        const target = returnFocus || document.getElementById('main');
        if (target) {
          if (!returnFocus) target.tabIndex = -1;
          target.focus({ preventScroll: true });
        }
      }
      returnFocus = null;
    }

    async function submitConsent(choice, consent) {
      if (saving) return;
      saving = true;
      lastAttempt = { choice, consent };
      const hadFocus = banner.contains(document.activeElement);
      status.textContent = status.dataset.saving;
      description.hidden = true;
      actions.hidden = true;
      preferencesPanel.hidden = true;
      recovery.hidden = true;
      if (hadFocus) status.focus({ preventScroll: true });
      try {
        const privacy = await loadCustomerPrivacy();
        await withDeadline(function (finish) {
          privacy.setTrackingConsent(consent, function (result) {
            finish(result && (result.error || result instanceof Error || typeof result === 'string'));
          });
        });
        storeChoice(choice, consent);
        setDismissed(false);
        hideBanner();
      } catch (_error) {
        status.textContent = status.dataset.error;
        recovery.hidden = false;
        if (banner.contains(document.activeElement)) retryButton.focus({ preventScroll: true });
      } finally {
        saving = false;
      }
    }

    function acceptCookies() {
      submitConsent('accept', {
        analytics: true,
        preferences: true,
        marketing: true
      });
    }

    function declineCookies() {
      submitConsent('decline', {
        analytics: false,
        preferences: false,
        marketing: false,
        sale_of_data: false
      });
    }

    function savePreferences() {
      const analyticsInput = banner.querySelector('[data-cookie-preference="analytics"]');
      const preferencesInput = banner.querySelector('[data-cookie-preference="preferences"]');
      const marketingInput = banner.querySelector('[data-cookie-preference="marketing"]');
      const analytics = Boolean(analyticsInput && analyticsInput.checked);
      const preferences = Boolean(preferencesInput && preferencesInput.checked);
      const marketing = Boolean(marketingInput && marketingInput.checked);

      submitConsent('custom', {
        analytics: analytics,
        preferences: preferences,
        marketing: marketing
      });
    }

    function togglePreferences() {
      if (!preferencesPanel || !manageButton) return;
      const isHidden = preferencesPanel.hidden;
      preferencesPanel.hidden = !isHidden;
      manageButton.setAttribute('aria-expanded', String(isHidden));
    }

    banner.addEventListener('pointerdown', useCustomControls, { once: true });
    banner.addEventListener('focusin', useCustomControls, { once: true });
    banner.addEventListener('click', useCustomControls, { capture: true, once: true });
    if (acceptButton) acceptButton.addEventListener('click', acceptCookies);
    if (declineButton) declineButton.addEventListener('click', declineCookies);
    if (manageButton) manageButton.addEventListener('click', togglePreferences);
    if (saveButton) saveButton.addEventListener('click', savePreferences);

    retryButton.addEventListener('click', function () {
      if (lastAttempt) submitConsent(lastAttempt.choice, lastAttempt.consent);
    });
    function closeWithoutSaving() {
      if (saving) return;
      if (lastAttempt) setDismissed(true);
      hideBanner();
    }
    closeButton.addEventListener('click', closeWithoutSaving);
    banner.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !saving && (lastAttempt || returnFocus)) {
        event.preventDefault();
        closeWithoutSaving();
      }
    });

    document.addEventListener('visitorConsentCollected', refreshBanner);
    document.querySelectorAll('a[href="#shopifyReshowConsentBanner"]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        if (document.documentElement.hasAttribute('data-cookie-native')) return;
        useCustomControls();
        event.preventDefault();
        event.stopImmediatePropagation();
        if (saving) return;
        returnFocus = link;
        resetControls();
        let consent = {};
        try { consent = getCustomerPrivacy().currentVisitorConsent() || {}; } catch (_error) {}
        purposes.forEach(function (purpose) {
          const input = banner.querySelector('[data-cookie-preference="' + purpose + '"]');
          if (input) input.checked = consent[purpose] === 'yes';
        });
        preferencesPanel.hidden = false;
        manageButton.setAttribute('aria-expanded', 'true');
        banner.hidden = false;
        manageButton.focus();
      });
    });
    loadCustomerPrivacy().then(refreshBanner, handleLoadFailure);
  }

  /* ---------- boot ---------- */
  function initAll() {
    initMobileMenu();
    initSmallActions();
    initScrub();
    initHomeVideo();
    initFilmLightbox();
    initQty();
    initGallery();
    initReveal();
    initVariantSelects();
    initStickyAtc();
    initHomeBuyBar();
    initFormNotes();
    initCartPage();
    initCookieBanner();
  }

  initAll();

  // The theme editor replaces one section's markup at a time. Re-running every
  // init is cheap and each one is a no-op for anything already bound.
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function (event) {
    if (!galleryState || !event.target.contains(galleryState.gallery)) return;
    if (galleryState.dialog && galleryState.dialog.open) galleryState.dialog.close();
    if (galleryState.resizeObserver) galleryState.resizeObserver.disconnect();
    galleryState = null;
    syncOverlayChrome();
  });
})();
